"use client";

import { useRouter, usePathname } from "next/navigation";
import { Check, ChevronDown, Globe } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LOCALES, LOCALE_META, type Locale } from "@/lib/i18n/config";
import { useI18n } from "@/lib/i18n/LanguageProvider";
import { cn } from "@/lib/utils";
import { track } from "@/lib/tracking";

const LanguageSwitcher = ({ className }: { className?: string }) => {
  const { locale } = useI18n();
  const router = useRouter();
  const pathname = usePathname();

  const switchTo = (target: Locale) => {
    if (target === locale) return;
    track("language_switch", { from: locale, to: target });
    const parts = pathname.split("/").filter(Boolean);
    if (parts.length && (LOCALES as string[]).includes(parts[0])) {
      parts[0] = target;
    } else {
      parts.unshift(target);
    }
    router.push("/" + parts.join("/"));
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          "inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-sm font-medium text-foreground transition-colors hover:bg-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          className
        )}
        aria-label="Change language"
      >
        <Globe className="h-4 w-4" />
        <span>{LOCALE_META[locale].short}</span>
        <ChevronDown className="h-3.5 w-3.5 opacity-70" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[10rem]">
        {LOCALES.map((code) => (
          <DropdownMenuItem
            key={code}
            onClick={() => switchTo(code)}
            className="flex items-center gap-2 cursor-pointer"
          >
            <span className="text-base leading-none">{LOCALE_META[code].flag}</span>
            <span className="flex-1">{LOCALE_META[code].label}</span>
            {locale === code && <Check className="h-4 w-4 text-primary" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSwitcher;
